// frontend/src/pages/PayrollGuide/styles.ts
// Palette + size configuration (varies based on whether page is embedded in a side panel).

// ── Palette ─────────────────────────────────────────────────────────────
export const C = {
  ink: "#12262B",
  text: "#1B2533",
  muted: "#66748B",
  faint: "#94A0B2",
  teal: "#15A08C",
  tealD: "#0F8474",
  tealInk: "#0E8A78",
  tealSoft: "#E3F4F0",
  line: "#E7EAF0",
  lineSoft: "#F1F3F7",
  surface: "#F4F6F8",
} as const;

export const FONT = "Inter, system-ui, sans-serif";

// ── Size config ─────────────────────────────────────────────────────────
/**
 * The page is rendered either standalone (full-width) or embedded in a narrow
 * side panel (e.g. in-app help drawer). Compact sizes are used for the panel.
 */
export interface SizeConfig {
  contentPad: string;
  contentMaxW: string | number;
  heroPad: string;
  heroH1: number;
  heroP: number;
  heroEyebrow: number;
  flowPad: string | number;
  flowTitle: number;
  stepNum: number;
  stepName: number;
  stepGap: number;
  secPad: string;
  secNum: number;
  secNumRadius: number;
  secH2: number;
  secTime: number;
  secBody: number;
  secLineH: number;
  whyPad: string;
  whyFont: number;
  whyLabel: number;
  btnPad: string;
  btnFont: number;
  faqTitle: number;
  faqMargin: string;
  faqSummaryPad: string;
  faqSummaryFont: number;
  faqAnswerPad: string;
  faqAnswerFont: number;
  nexaPad: string | number;
  nexaAvatar: number;
  nexaH3: number;
  nexaP: number;
  nexaBtnPad: string;
  nexaBtnFont: number;
  logoSize: number;
  brandName: number;
}

/**
 * Build the size config for the current render context.
 * `embeddedInPanel: true` uses compact sizes for a narrow side-panel layout.
 */
export function buildSizeConfig(embeddedInPanel: boolean): SizeConfig {
  if (embeddedInPanel) {
    return {
      contentPad: "20px 22px 50px",
      contentMaxW: "100%",
      heroPad: "20px 18px",
      heroH1: 21,
      heroP: 13,
      heroEyebrow: 10.5,
      flowPad: 16,
      flowTitle: 11,
      stepNum: 28,
      stepName: 13.5,
      stepGap: 8,
      secPad: "18px 18px",
      secNum: 28,
      secNumRadius: 7,
      secH2: 15,
      secTime: 10.5,
      secBody: 13,
      secLineH: 1.6,
      whyPad: "11px 13px",
      whyFont: 12,
      whyLabel: 10,
      btnPad: "8px 14px",
      btnFont: 12.5,
      faqTitle: 11,
      faqMargin: "22px 0 10px",
      faqSummaryPad: "12px 16px",
      faqSummaryFont: 13,
      faqAnswerPad: "11px 16px 14px",
      faqAnswerFont: 12.5,
      nexaPad: 18,
      nexaAvatar: 44,
      nexaH3: 14,
      nexaP: 12,
      nexaBtnPad: "9px 18px",
      nexaBtnFont: 13,
      logoSize: 38,
      brandName: 19,
    };
  }
  return {
    contentPad: "28px 32px 100px",
    contentMaxW: 900,
    heroPad: "34px 30px",
    heroH1: 32,
    heroP: 15.5,
    heroEyebrow: 11,
    flowPad: "24px 28px",
    flowTitle: 13,
    stepNum: 36,
    stepName: 12.5,
    stepGap: 8,
    secPad: "28px 30px",
    secNum: 32,
    secNumRadius: 8,
    secH2: 19,
    secTime: 12,
    secBody: 14.5,
    secLineH: 1.7,
    whyPad: "14px 18px",
    whyFont: 13.5,
    whyLabel: 11.5,
    btnPad: "10px 18px",
    btnFont: 13.5,
    faqTitle: 13,
    faqMargin: "36px 0 14px",
    faqSummaryPad: "16px 20px",
    faqSummaryFont: 14.5,
    faqAnswerPad: "14px 20px 18px",
    faqAnswerFont: 13.5,
    nexaPad: "28px 30px",
    nexaAvatar: 56,
    nexaH3: 17,
    nexaP: 13.5,
    nexaBtnPad: "11px 22px",
    nexaBtnFont: 14,
    logoSize: 44,
    brandName: 22,
  };
}